import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import {
  Ticket, ArrowRight, QrCode, ScanLine, CreditCard, Tags, LayoutDashboard,
  BarChart3, Users, CalendarCheck, Send, Building2, Globe, ShieldCheck,
  CheckCircle2, Landmark, FerrisWheel, MapPin, Download, Zap, TrendingUp, Minus,
} from 'lucide-react';

const H = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const IMG_HERO = 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';
const IMG_SCAN = 'https://images.pexels.com/photos/16345589/pexels-photo-16345589.jpeg?auto=compress&cs=tinysrgb&w=1200';
const IMG_DASH = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

/* ── lightweight scroll reveal (no framer-motion dependency) ── */
const Reveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</Tag>;
};

const FAQS = [
  { q: 'How much does StayMii E-Ticket cost?', a: 'There are four plans: StayMii E-Ticket at RM299/month (core ticketing, 0% transaction fee on direct & manual bookings); StayMii E-Ticket + E-Invoice at RM399/month (adds LHDN/MyInvois e-Invoicing, 0% fee); StayMii E-Ticket + SGMC at RM399/month + 3% on SGMC-channel bookings (adds global distribution via SGMC); and the recommended StayMii Full Suite at RM499/month + 3% on SGMC bookings (E-Ticket + E-Invoice + SGMC together). No setup fees and you can cancel anytime.' },
  { q: 'How do customers pay for tickets?', a: 'Checkout is powered by Stripe. Customers pay by card, and for Malaysian Ringgit (MYR) we also enable FPX online banking and GrabPay. Funds settle directly into your own Stripe account.' },
  { q: 'How is the QR e-ticket delivered?', a: 'The instant a payment succeeds, a unique, tamper-proof QR e-ticket is generated and emailed to the buyer. You can also re-send it or forward it to their WhatsApp from the dashboard.' },
  { q: 'Can a ticket be scanned more than once?', a: 'No. Every QR can only be admitted once, and only on the visit date the customer chose — so tickets cannot be reused, duplicated or scanned early.' },
  { q: 'Do my staff need special scanning hardware?', a: 'No. Any smartphone or tablet becomes a professional gate scanner straight from the browser. Add staff as scanner users, each with their own login.' },
  { q: 'Can I run multiple businesses on one platform?', a: 'Yes. StayMii E-Ticket is fully multi-tenant — every company gets its own branded booking link, isolated data, users, reports and settings.' },
  { q: 'Is StayMii E-Ticket built in Malaysia?', a: 'Yes. StayMii is a MySTI-certified, Malaysian-developed technology product, purpose-built for local payment rails (FPX, GrabPay) and LHDN e-invoicing.' },
];

// Four-plan structure. SGMC = StayMii Global Market Connect.
const PLANS = [
  {
    id: 'eticket',
    name: 'StayMii E-Ticket',
    tagline: 'Ticketing',
    price: 'RM299',
    period: '/month',
    fee: '0% transaction fee',
    feePositive: true,
    intro: 'Core ticketing package for attractions and venues.',
    features: [
      'E-Ticket management', 'QR code ticket generation', 'QR admission scanning',
      'Ticket / product management', 'Rates', 'Inventory / capacity management',
      'Booking management', 'Visit date & time management', 'Booking reports',
      'Admission status', 'Basic operational reporting',
    ],
    feeNote: '0% fee on direct & manual bookings.',
  },
  {
    id: 'eticket_einvoice',
    name: 'StayMii E-Ticket + E-Invoice',
    tagline: 'Ticketing + E-Invoice',
    price: 'RM399',
    period: '/month',
    fee: '0% transaction fee',
    feePositive: true,
    inheritsLabel: 'Everything in E-Ticket, plus:',
    features: [
      'StayMii E-Invoice', 'LHDN / MyInvois e-Invoice integration', 'E-Invoice generation',
      'E-Invoice status management', 'E-Invoice records & reporting',
    ],
    feeNote: '+ RM100/month vs E-Ticket · 0% transaction fee.',
  },
  {
    id: 'eticket_sgmc',
    name: 'StayMii E-Ticket + SGMC',
    tagline: 'Ticketing + Global Distribution',
    price: 'RM399',
    period: '/month',
    fee: '+ 3% on SGMC bookings',
    inheritsLabel: 'Everything in E-Ticket, plus:',
    features: [
      'Global distribution connectivity', 'Channel-manager connectivity', 'Product mapping',
      'Rate mapping', 'Availability synchronization', 'Booking synchronization',
      'Cancellation & amendment synchronization', 'Channel reporting', 'Supported provider connections',
    ],
    providers: ['Supported channel-management / distribution providers'],
    feeNote: '3% on successful SGMC-channel bookings only. No fee on walk-ins, manual, direct counter or direct bookings.',
  },
  {
    id: 'full_suite',
    name: 'StayMii Full Suite',
    tagline: 'Everything, together',
    price: 'RM499',
    period: '/month',
    fee: '+ 3% on SGMC bookings',
    featured: true,
    inheritsLabel: 'Everything included:',
    features: [
      'StayMii E-Ticket', 'QR ticketing', 'Inventory management', 'Booking management',
      'Admission scanning', 'Reports', 'StayMii E-Invoice', 'SGMC — Global Market Connect',
    ],
    feeNote: '3% on successful SGMC bookings.',
  },
];

const PLAN_SUMMARY = [
  { name: 'StayMii E-Ticket', monthly: 'RM299', fee: '0%' },
  { name: 'StayMii E-Ticket + E-Invoice', monthly: 'RM399', fee: '0%' },
  { name: 'StayMii E-Ticket + SGMC', monthly: 'RM399', fee: '3%' },
  { name: 'StayMii Full Suite', monthly: 'RM499', fee: '3%', featured: true },
];

// Full feature-by-feature comparison. Column order = [E-Ticket, +E-Invoice, +SGMC, Full Suite].
const COMPARE_COLS = [
  { id: 'eticket', name: 'E-Ticket', price: 'RM299', fee: '0%' },
  { id: 'eticket_einvoice', name: '+ E-Invoice', price: 'RM399', fee: '0%' },
  { id: 'eticket_sgmc', name: '+ SGMC', price: 'RM399', fee: '3%' },
  { id: 'full_suite', name: 'Full Suite', price: 'RM499', fee: '3%', featured: true },
];

const COMPARE_GROUPS = [
  {
    group: 'Ticketing',
    rows: [
      ['E-Ticket management', [1, 1, 1, 1]],
      ['QR code ticket generation', [1, 1, 1, 1]],
      ['QR admission scanning', [1, 1, 1, 1]],
      ['Ticket / product management', [1, 1, 1, 1]],
      ['Rates', [1, 1, 1, 1]],
      ['Inventory / capacity management', [1, 1, 1, 1]],
      ['Booking management', [1, 1, 1, 1]],
      ['Visit date & time management', [1, 1, 1, 1]],
      ['Booking reports', [1, 1, 1, 1]],
      ['Admission status', [1, 1, 1, 1]],
      ['Basic operational reporting', [1, 1, 1, 1]],
    ],
  },
  {
    group: 'E-Invoice (LHDN / MyInvois)',
    rows: [
      ['StayMii E-Invoice', [0, 1, 0, 1]],
      ['LHDN / MyInvois e-Invoice integration', [0, 1, 0, 1]],
      ['E-Invoice generation', [0, 1, 0, 1]],
      ['E-Invoice status management', [0, 1, 0, 1]],
      ['E-Invoice records & reporting', [0, 1, 0, 1]],
    ],
  },
  {
    group: 'SGMC — Global Market Connect',
    rows: [
      ['Global distribution connectivity', [0, 0, 1, 1]],
      ['Channel-manager connectivity', [0, 0, 1, 1]],
      ['Product mapping', [0, 0, 1, 1]],
      ['Rate mapping', [0, 0, 1, 1]],
      ['Availability synchronization', [0, 0, 1, 1]],
      ['Booking synchronization', [0, 0, 1, 1]],
      ['Cancellation & amendment synchronization', [0, 0, 1, 1]],
      ['Channel reporting', [0, 0, 1, 1]],
      ['Supported provider connections', [0, 0, 1, 1]],
    ],
  },
];

const EticketProduct = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [subLoading, setSubLoading] = useState('');
  const startSubscription = async (planId = 'full_suite') => {
    if (subLoading) return;
    setSubLoading(planId);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API}/api/eticket/subscription/checkout`, { origin_url: window.location.origin, plan_id: planId });
      if (res.data?.url) window.location.href = res.data.url;
      else setSubLoading('');
    } catch (e) {
      setSubLoading('');
      alert('Could not start checkout. Please try again in a moment.');
    }
  };

  /* FAQ rich-snippet schema for Google */
  useEffect(() => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-eticket-faq', 'true');
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    document.head.appendChild(s);
    return () => { document.head.querySelector('script[data-eticket-faq]')?.remove(); };
  }, []);

  /* hero ticket 3D tilt */
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const onTilt = (e) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -10, y: px * 12 });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="eticket-product-page">
      <SEO
        title="Smart E-Ticketing System for Malaysian Events & Attractions"
        description="StayMii E-Ticket lets attractions, tours and events sell tickets online with Stripe (Cards, FPX, GrabPay), auto-issue tamper-proof QR e-tickets, and scan them at the gate. Multi-tenant ticketing, built in Malaysia."
        keywords="e-ticket malaysia, online ticketing system, QR ticket scanner, event ticketing software malaysia, attraction ticketing, sell tickets online, stripe fpx grabpay ticketing, qr code entry system"
        canonicalUrl="/e-ticket"
      />
      <LandingNavBar />

      <StyleTag />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative overflow-hidden pt-28 pb-24 lg:pt-36 lg:pb-32 bg-gradient-to-b from-cyan-50 via-white to-white" data-testid="eticket-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[560px] h-[560px] bg-cyan-300/25 rounded-full blur-[150px]" />
          <div className="absolute -bottom-24 -left-20 w-[460px] h-[460px] bg-blue-300/20 rounded-full blur-[140px]" />
          <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: 'linear-gradient(#0f172a0d 1px,transparent 1px),linear-gradient(90deg,#0f172a0d 1px,transparent 1px)', backgroundSize: '48px 48px', maskImage: 'radial-gradient(ellipse at 50% 30%, black, transparent 75%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-cyan-200 shadow-sm mb-7 reveal reveal-in" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              <span className="text-cyan-700 text-[11px] font-bold uppercase tracking-[0.2em]" style={MONO}>StayMii&trade; · E-Ticket</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900 mb-6" style={H} data-testid="hero-title">
              Sell tickets online.<br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-cyan-500 via-cyan-600 to-blue-600 bg-clip-text text-transparent">Scan them at the gate.</span>
              </span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-9 font-medium">
              A complete e-ticketing system for Malaysian attractions, tours &amp; events. Launch a branded booking page,
              take <span className="text-cyan-700 font-semibold">Card · FPX · GrabPay</span> payments through Stripe, and admit visitors
              with tamper-proof QR codes — all in one platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-9">
              <Link to="/auth/signup?category=eticket" data-testid="hero-cta-signup"
                className="cta-glow group inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl font-black text-sm tracking-wide transition-colors">
                Start Free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-xl font-semibold text-sm shadow-sm transition-colors">
                Book a demo
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500 font-medium" data-testid="hero-trust">
              {['Stripe secure checkout', 'FPX & GrabPay ready', 'MySTI Certified', 'Built in Malaysia'].map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cyan-500" />{t}</span>
              ))}
            </div>
          </div>

          {/* Floating 3D ticket stub (navy accent on bright bg) */}
          <div className="lg:col-span-5 flex justify-center" style={{ perspective: '1200px' }}>
            <div
              onMouseMove={onTilt}
              onMouseLeave={() => setTilt({ x: 0, y: 0 })}
              style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, transition: 'transform .2s ease' }}
              className="ticket-stub relative w-full max-w-sm bg-[#0C2159] border border-cyan-400/30 rounded-3xl p-7 shadow-[0_25px_60px_-20px_rgba(12,33,89,0.6)]"
              data-testid="hero-ticket-graphic"
            >
              <span className="notch left" /><span className="notch right" />
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-cyan-300"><Ticket className="w-4 h-4" /><span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={MONO}>Admit One</span></div>
                <span className="text-[10px] font-bold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 px-2 py-0.5 rounded" style={MONO}>PAID</span>
              </div>
              <h3 className="text-xl font-extrabold text-white" style={H}>Sunrise Adventure Park</h3>
              <p className="text-[11px] text-slate-400 mt-1" style={MONO}>REF · STM-4821-QR · 2 PAX</p>

              <div className="grid grid-cols-2 gap-3 mt-5">
                {[['ADULT', 'MYR 45.00'], ['CHILD', 'MYR 25.00']].map(([n, p]) => (
                  <div key={n} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
                    <div className="text-[9px] text-slate-400 tracking-widest" style={MONO}>{n}</div>
                    <div className="text-sm font-bold text-cyan-300" style={MONO}>{p}</div>
                  </div>
                ))}
              </div>

              <div className="relative mt-5 mx-auto w-40 h-40 rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                <QrCode className="w-32 h-32 text-[#0C2159]" strokeWidth={1.1} />
                <span className="scan-beam absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent shadow-[0_0_12px_2px_rgba(6,182,212,0.7)]" />
              </div>
              <p className="text-center text-[10px] text-slate-500 mt-4 tracking-widest" style={MONO}>VALID · 12 JUN 2026 ONLY</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MARQUEE STRIP ═══════════════ */}
      <div className="border-y border-slate-100 bg-slate-50 py-5 overflow-hidden" data-testid="trust-marquee">
        <div className="flex items-center gap-10 marquee whitespace-nowrap text-slate-400">
          {['STRIPE SECURE', 'FPX ONLINE BANKING', 'GRABPAY', 'INSTANT QR E-TICKETS', 'MYSTI CERTIFIED', 'WHATSAPP DELIVERY', 'MULTI-TENANT SAAS', 'EXCEL & PDF REPORTS', 'STRIPE SECURE', 'FPX ONLINE BANKING', 'GRABPAY', 'INSTANT QR E-TICKETS', 'MYSTI CERTIFIED', 'WHATSAPP DELIVERY', 'MULTI-TENANT SAAS', 'EXCEL & PDF REPORTS'].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.2em]" style={MONO}>
              <Zap className="w-3.5 h-3.5 text-cyan-500" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════ BENTO FEATURES ═══════════════ */}
      <section className="py-24 lg:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>The Complete Toolkit</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>
              Not a payment link. A full ticketing operating system.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5 auto-rows-[minmax(0,auto)]">

            {/* QR scanner — hero bento */}
            <Reveal className="md:col-span-6 lg:col-span-8 lg:row-span-2">
              <div className="group h-full rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm" data-testid="bento-qr-scanner">
                <div className="relative h-52 overflow-hidden">
                  <img src={IMG_SCAN} alt="A person scanning a QR code e-ticket with a smartphone at an entrance" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/30 to-transparent" />
                </div>
                <div className="p-8 lg:p-10">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-500 flex items-center justify-center mb-5 shadow-lg shadow-cyan-500/30"><ScanLine className="w-6 h-6 text-white" /></div>
                  <h3 className="text-2xl font-bold text-slate-900 tracking-tight mb-2" style={H}>Gate scanning &amp; admission control</h3>
                  <p className="text-slate-600 max-w-lg leading-relaxed">Turn any phone into a scanner. Each QR is admitted exactly once, only on its valid date — with instant <span className="text-cyan-700 font-semibold">valid · admitted · unpaid · wrong-date</span> feedback. No fraud, no double entry.</p>
                </div>
              </div>
            </Reveal>

            {/* Dashboard */}
            <Reveal delay={80} className="md:col-span-3 lg:col-span-4 lg:row-span-2">
              <div className="h-full rounded-3xl border border-slate-200 overflow-hidden bg-white shadow-sm" data-testid="bento-dashboard">
                <div className="relative h-32 overflow-hidden">
                  <img src={IMG_DASH} alt="Real-time analytics dashboard with charts" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                </div>
                <div className="p-7">
                  <div className="w-11 h-11 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center mb-4"><LayoutDashboard className="w-5 h-5 text-cyan-600" /></div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight mb-2" style={H}>Real-time dashboard</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-5">Bookings, revenue, today’s visitors and ticket sales — live.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[['1.2k', 'BOOKINGS'], ['RM48k', 'REVENUE'], ['312', 'TODAY']].map(([v, l]) => (
                      <div key={l} className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2 text-center">
                        <div className="text-cyan-700 font-bold text-sm" style={MONO}>{v}</div>
                        <div className="text-[8px] text-slate-400 tracking-widest mt-0.5" style={MONO}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            {/* small cards */}
            {[
              { icon: CreditCard, t: 'Stripe payments', d: 'Cards + FPX + GrabPay for MYR. Funds to your own account.', span: 'lg:col-span-4' },
              { icon: Tags, t: 'Multi-tier pricing', d: 'Unlimited ticket types — Adult, Child, Group. Pause any rate.', span: 'lg:col-span-4' },
              { icon: CalendarCheck, t: 'Date-valid tickets', d: 'Scannable only on the chosen visit date. No reuse.', span: 'lg:col-span-4' },
              { icon: QrCode, t: 'Instant QR e-tickets', d: 'Tamper-proof QR generated on every paid booking.', span: 'lg:col-span-3' },
              { icon: Send, t: 'Email & WhatsApp', d: 'Auto-delivered, with one-click resend & share.', span: 'lg:col-span-3' },
              { icon: BarChart3, t: 'Reports & exports', d: 'Daily / monthly / annual → Excel & PDF.', span: 'lg:col-span-3' },
              { icon: Users, t: 'Team users & roles', d: 'Owner + staff scanners, each with a login.', span: 'lg:col-span-3' },
              { icon: Globe, t: 'Branded booking page', d: 'Your logo, banner & custom StayMii link.', span: 'lg:col-span-4' },
              { icon: Download, t: 'Booking QR poster', d: 'Download a print-ready QR for your counter.', span: 'lg:col-span-4' },
              { icon: Building2, t: 'Multi-tenant SaaS', d: 'Isolated workspace per company. Built to scale.', span: 'lg:col-span-4' },
            ].map((f, i) => (
              <Reveal key={i} delay={(i % 3) * 60} className={`md:col-span-3 ${f.span}`}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-cyan-400 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`bento-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center mb-4 group-hover:bg-cyan-500 group-hover:border-cyan-500 transition-colors">
                    <f.icon className="w-5 h-5 text-cyan-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1.5" style={H}>{f.t}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS ═══════════════ */}
      <section className="py-24 bg-cyan-50 border-y border-cyan-100" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-xl">
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>From zero to first sale</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>Live in four steps</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: '01', t: 'Create your workspace', d: 'Register your company & branding once.' },
              { n: '02', t: 'Add ticket types', d: 'Set your rates and prices. Toggle any on or off.' },
              { n: '03', t: 'Share link or QR', d: 'Post your booking link or print the QR poster.' },
              { n: '04', t: 'Scan at the gate', d: 'Staff admit each ticket once, on the valid date.' },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full rounded-2xl border border-cyan-100 bg-white shadow-sm p-7" data-testid={`step-${i}`}>
                  <span className="text-4xl font-black text-cyan-300" style={MONO}>{s.n}</span>
                  <h3 className="text-slate-900 font-bold mt-3 mb-1.5" style={H}>{s.t}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ USE CASES ═══════════════ */}
      <section className="py-24 lg:py-32 bg-white" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>Built for</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-6" style={H}>
              Any business that sells admission
            </h2>
            <p className="text-slate-500 leading-relaxed mb-8 max-w-lg">
              Each business runs on its own isolated, branded workspace — with a custom booking slug, its own team and its own reports. One platform, unlimited operators.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: FerrisWheel, l: 'Theme parks & attractions' },
                { icon: Landmark, l: 'Museums & galleries' },
                { icon: MapPin, l: 'Tours & activities' },
                { icon: Ticket, l: 'Events & workshops' },
                { icon: TrendingUp, l: 'Paid experiences' },
                { icon: Users, l: 'Community events' },
              ].map((u, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3.5" data-testid={`usecase-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-cyan-50 border border-cyan-100 flex items-center justify-center shrink-0"><u.icon className="w-4 h-4 text-cyan-600" /></div>
                  <span className="text-sm font-semibold text-slate-700">{u.l}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="ticket-stub relative rounded-3xl bg-[#0C2159] border border-cyan-400/20 p-8 shadow-[0_25px_60px_-20px_rgba(12,33,89,0.55)]" data-testid="multitenant-graphic">
              <span className="notch left white" /><span className="notch right white" />
              <p className="text-[10px] text-cyan-300 tracking-[0.2em] mb-4" style={MONO}>// YOUR PUBLIC BOOKING LINK</p>
              {['sunrise-adventure-park', 'penang-heritage-museum', 'langkawi-island-tours'].map((slug) => (
                <div key={slug} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 mb-3">
                  <Globe className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-slate-300 truncate" style={MONO}>staymii.com/eticket/<span className="text-cyan-300">{slug}</span></span>
                </div>
              ))}
              <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400" style={MONO}><ShieldCheck className="w-4 h-4 text-emerald-400" /> ISOLATED DATA · OWN TEAM · OWN REPORTS</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ PRICING ═══════════════ */}
      <section className="py-24 lg:py-28 bg-white border-t border-slate-100" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-4">
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>Simple pricing</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>Pick the plan that fits your venue.</h2>
            <p className="text-slate-500 mt-4">RM299 ticketing · RM399 + e-Invoice · RM399 + global distribution · RM499 full suite. No setup fees. Cancel anytime.</p>
          </Reveal>

          {/* Quick summary table */}
          <Reveal delay={60}>
            <div className="max-w-2xl mx-auto mb-14 overflow-hidden rounded-2xl border border-slate-200 shadow-sm" data-testid="pricing-summary-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white" style={MONO}>
                    <th className="text-left px-5 py-3 font-semibold">Plan</th>
                    <th className="text-right px-5 py-3 font-semibold">Monthly</th>
                    <th className="text-right px-5 py-3 font-semibold">SGMC fee</th>
                  </tr>
                </thead>
                <tbody>
                  {PLAN_SUMMARY.map((r, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${r.featured ? 'bg-cyan-50' : 'bg-white'}`}>
                      <td className="px-5 py-3 font-semibold text-slate-800">{r.name}{r.featured && <span className="ml-2 text-[10px] font-bold text-cyan-700 uppercase tracking-widest">Recommended</span>}</td>
                      <td className="px-5 py-3 text-right font-black text-slate-900" style={H}>{r.monthly}</td>
                      <td className={`px-5 py-3 text-right font-bold ${r.fee === '0%' ? 'text-emerald-600' : 'text-slate-700'}`}>{r.fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          {/* Plan cards */}
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
            {PLANS.map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <div
                  className={`relative flex flex-col h-full rounded-3xl border p-6 lg:p-7 transition-all ${p.featured ? 'border-cyan-400 bg-gradient-to-b from-cyan-50 to-white shadow-2xl ring-2 ring-cyan-200 md:scale-[1.02]' : 'border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1'}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {p.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-cyan-500 text-white text-[10px] font-bold tracking-widest px-3 py-1 rounded-full shadow" style={MONO}>
                      <Zap className="w-3 h-3" /> RECOMMENDED
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <Ticket className={`w-4 h-4 ${p.featured ? 'text-cyan-600' : 'text-slate-400'}`} />
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide" style={MONO}>{p.tagline}</span>
                  </div>
                  <h3 className="text-lg font-extrabold text-slate-900 leading-tight mb-3" style={H}>{p.name}</h3>
                  <div className="flex items-end gap-1.5 mb-1">
                    <span className="text-4xl font-black text-slate-900 tracking-tighter" style={H}>{p.price}</span>
                    <span className="text-slate-400 font-semibold text-sm mb-1.5">{p.period}</span>
                  </div>
                  <div className={`inline-flex self-start items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full mb-4 ${p.feePositive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {p.fee}
                  </div>
                  {p.intro && <p className="text-xs text-slate-500 mb-4 leading-relaxed">{p.intro}</p>}
                  {p.inheritsLabel && <p className="text-[11px] font-bold text-cyan-700 uppercase tracking-wide mb-3" style={MONO}>{p.inheritsLabel}</p>}

                  <ul className="space-y-2 mb-4">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-[13px] text-slate-700">
                        <CheckCircle2 className={`shrink-0 mt-0.5 ${p.featured ? 'text-cyan-500' : 'text-slate-400'}`} style={{ width: 16, height: 16 }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {p.providers && (
                    <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5" style={MONO}>Supported providers</p>
                      <div className="flex flex-wrap gap-1.5">
                        {p.providers.map((pr, k) => (
                          <span key={k} className="text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2 py-0.5">{pr}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-[11px] text-slate-400 leading-relaxed mb-5">{p.feeNote}</p>

                  <button
                    onClick={() => startSubscription(p.id)}
                    disabled={!!subLoading}
                    data-testid={`plan-cta-${p.id}`}
                    className={`mt-auto w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-black text-sm transition-colors disabled:opacity-70 ${p.featured ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                  >
                    {subLoading === p.id ? 'Redirecting…' : <>Subscribe {p.price} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          {/* SGMC marketing note */}
          <Reveal delay={120}>
            <div className="mt-14 max-w-3xl mx-auto rounded-2xl border border-cyan-200 bg-cyan-50/60 p-6 lg:p-7" data-testid="sgmc-note">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-5 h-5 text-cyan-600" />
                <span className="text-sm font-extrabold text-slate-900" style={H}>SGMC — StayMii Global Market Connect</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Connect your StayMii E-Ticket inventory to global distribution networks through SGMC. Sync availability, rates and bookings with supported channel-management and distribution providers. The 3% fee applies only to successful bookings generated through SGMC-connected channels — never to walk-ins, manual, direct counter or direct bookings.
              </p>
            </div>
          </Reveal>

          <p className="text-center text-xs text-slate-400 mt-8">Billed monthly in MYR · Prices exclude applicable tax · No setup fees · Cancel anytime</p>
        </div>
      </section>

      {/* ═══════════════ FEATURE COMPARISON ═══════════════ */}
      <section className="py-24 lg:py-28 bg-slate-50 border-t border-slate-100" data-testid="comparison-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>Compare plans</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>Exactly what each tier adds</h2>
            <p className="text-slate-500 mt-4">Every plan includes full ticketing. E-Invoice adds LHDN compliance; SGMC adds global distribution.</p>
          </Reveal>

          <Reveal delay={60}>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white" data-testid="comparison-table">
              <table className="w-full min-w-[720px] text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-slate-900 text-left text-white px-5 py-4 font-semibold rounded-tl-2xl" style={MONO}>Feature</th>
                    {COMPARE_COLS.map((c, i) => (
                      <th key={c.id} className={`px-4 py-4 text-center ${c.featured ? 'bg-cyan-600' : 'bg-slate-900'} text-white ${i === COMPARE_COLS.length - 1 ? 'rounded-tr-2xl' : ''}`}>
                        <div className="font-extrabold" style={H}>{c.name}</div>
                        <div className="text-[11px] font-normal opacity-90 mt-0.5">{c.price}/mo · {c.fee} fee</div>
                        {c.featured && <div className="text-[9px] font-bold tracking-widest mt-1 text-cyan-100">RECOMMENDED</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_GROUPS.map((grp, gi) => (
                    <React.Fragment key={gi}>
                      <tr>
                        <td colSpan={5} className="bg-slate-100 px-5 py-2.5 text-[11px] font-bold text-slate-600 uppercase tracking-widest" style={MONO}>{grp.group}</td>
                      </tr>
                      {grp.rows.map(([label, vals], ri) => (
                        <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/60">
                          <td className="sticky left-0 z-10 bg-white px-5 py-3 text-slate-700 font-medium">{label}</td>
                          {vals.map((v, vi) => (
                            <td key={vi} className={`px-4 py-3 text-center ${COMPARE_COLS[vi].featured ? 'bg-cyan-50/50' : ''}`}>
                              {v
                                ? <CheckCircle2 className="inline-block text-cyan-500" style={{ width: 18, height: 18 }} />
                                : <Minus className="inline-block text-slate-300" style={{ width: 16, height: 16 }} />}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {/* Pricing footer rows */}
                  <tr className="border-t-2 border-slate-200">
                    <td className="sticky left-0 z-10 bg-white px-5 py-4 font-bold text-slate-900">Monthly price</td>
                    {COMPARE_COLS.map((c) => (
                      <td key={c.id} className={`px-4 py-4 text-center font-black text-slate-900 ${c.featured ? 'bg-cyan-50/50' : ''}`} style={H}>{c.price}</td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="sticky left-0 z-10 bg-white px-5 py-4 font-bold text-slate-900">SGMC transaction fee</td>
                    {COMPARE_COLS.map((c) => (
                      <td key={c.id} className={`px-4 py-4 text-center font-bold ${c.fee === '0%' ? 'text-emerald-600' : 'text-slate-700'} ${c.featured ? 'bg-cyan-50/50' : ''}`}>{c.fee}</td>
                    ))}
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="sticky left-0 z-10 bg-white px-5 py-4 rounded-bl-2xl"></td>
                    {COMPARE_COLS.map((c, i) => (
                      <td key={c.id} className={`px-3 py-4 text-center ${c.featured ? 'bg-cyan-50/50' : ''} ${i === COMPARE_COLS.length - 1 ? 'rounded-br-2xl' : ''}`}>
                        <button onClick={() => startSubscription(c.id)} disabled={!!subLoading} data-testid={`compare-cta-${c.id}`}
                          className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-black text-xs transition-colors disabled:opacity-70 ${c.featured ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                          {subLoading === c.id ? '…' : 'Choose'}
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Reveal>
          <p className="text-center text-xs text-slate-400 mt-6">3% SGMC fee applies only to successful bookings via SGMC-connected channels — never to walk-ins, manual, direct counter or direct bookings.</p>
        </div>
      </section>

      {/* ═══════════════ FAQ + BUILT IN MALAYSIA ═══════════════ */}
      <section className="py-24 bg-slate-50 border-t border-slate-100" data-testid="faq-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <p className="text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={MONO}>FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-5" style={H}>Questions, answered</h2>
            <p className="text-slate-500 leading-relaxed mb-6">Everything you need to know about running ticketing on StayMii.</p>
            <div className="inline-flex items-center gap-3 rounded-xl border border-cyan-200 bg-white shadow-sm px-4 py-3">
              <ShieldCheck className="w-5 h-5 text-cyan-600" />
              <span className="text-sm text-slate-700 font-semibold">MySTI Certified · Built in Malaysia 🇲🇾</span>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-6" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-slate-900 font-bold hover:no-underline text-base py-5" style={H}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-500 leading-relaxed text-sm pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-cyan-500 via-cyan-500 to-blue-600" data-testid="final-cta">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <Reveal className="relative max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-white items-center justify-center mb-6 shadow-xl"><Ticket className="w-7 h-7 text-cyan-600" /></div>
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter mb-5" style={H}>Start selling tickets today</h2>
          <p className="text-white/90 leading-relaxed max-w-lg mx-auto mb-9">
            Launch your branded booking page, add your prices and go live in minutes. No setup fees — you pay only when you sell.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth/signup?category=eticket" data-testid="cta-start-bottom"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-white hover:bg-slate-50 text-cyan-700 rounded-xl font-black text-sm transition-colors shadow-lg">
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" data-testid="cta-pricing-bottom"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-cyan-600/40 hover:bg-cyan-600/60 text-white border border-white/40 rounded-xl font-semibold text-sm transition-colors">
              View pricing
            </Link>
          </div>
        </Reveal>
      </section>

      <LandingFooter />
    </div>
  );
};

/* ── scoped styles: keyframes, reveal, ticket perforations ── */
const StyleTag = () => (
  <style>{`
    .reveal{opacity:0;transform:translateY(26px);transition:opacity .7s cubic-bezier(.2,.7,.2,1),transform .7s cubic-bezier(.2,.7,.2,1)}
    .reveal-in{opacity:1;transform:none}
    .cta-glow{animation:ctaPulse 2.8s ease-in-out infinite}
    @keyframes ctaPulse{0%,100%{box-shadow:0 0 0 0 rgba(6,182,212,.35)}50%{box-shadow:0 0 26px 3px rgba(6,182,212,.45)}}
    @keyframes scanbeam{0%{top:6%;opacity:0}15%{opacity:1}85%{opacity:1}100%{top:92%;opacity:0}}
    .scan-beam{top:6%;animation:scanbeam 2.6s ease-in-out infinite}
    .ticket-stub .notch{position:absolute;top:50%;width:26px;height:26px;border-radius:9999px;background:#ffffff;transform:translateY(-50%);z-index:2}
    .ticket-stub .notch.left{left:-13px}
    .ticket-stub .notch.right{right:-13px}
    .marquee{animation:marq 32s linear infinite;width:max-content}
    @keyframes marq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    @media (prefers-reduced-motion: reduce){
      .reveal{opacity:1;transform:none;transition:none}
      .cta-glow,.scan-beam,.marquee{animation:none}
    }
  `}</style>
);

export default EticketProduct;
